//******************************************************************************
/**
 * @fileoverview DataPngBase - DataPNG基底クラス
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @version 0.2.1
 * @since 2026-02-01
 * @copyright 2026 KITAO Kaoru
 * @license MIT
 * @remarks 基底クラスにつき必ず継承して使用する
 * * @history
 * - 2026-02-03 v0.2.1 コメント・体裁調整
 * - 2026-02-01 v0.2.0 decode系関数を外部で共通利用可能できるよう変更
 * - 2026-01-28 v0.1.1 型定義厳密化
 * - 2026-01-27 v0.1.0 リリース
 */

//******************************************************************************
// 定数群
const CONSTANTS = Object.freeze({
	ELEMENTS: 4,
	MOD_24: 2 ** 24,
	MOD_48: 2 ** 48,
	HALF_24: 2 ** 23,
	HALF_48: 2 ** 47
});

//******************************************************************************
/**
 * RGBA配列を24bit符号なし数値に変換
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {number}
 */
const rgbaToUnsigned = (rgbaData) => {
	//--------------------------------------------------------------------------
	// 計算結果を返す
	return rgbaData[0] * 65536 + rgbaData[1] * 256 + rgbaData[2];
}

//******************************************************************************
/**
 * RGBAをそのまま返す
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {Uint8Array|Uint8ClampedArray}
 */
const decodeRgba = (rgbaData) => {
	// 取得した色配列をそのまま返す
	return rgbaData;
}

//******************************************************************************
/**
 * 不明の場合もRGBAをそのまま返す
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {Uint8Array|Uint8ClampedArray}
 */
const decodeUndefined = (rgbaData) => {
	//--------------------------------------------------------------------------
	// 取得した色配列をそのまま返す
	return rgbaData;
}

//******************************************************************************
/**
 * 24bit符号なし整数のデコード
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {number}
 */
const decodeUint24 = (rgbaData) => {
	//--------------------------------------------------------------------------
	// rgbaデータを取得して変換して返す
	return rgbaToUnsigned(rgbaData);
}

//******************************************************************************
/**
 * 24bit符号付き整数のデコード
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {number}
 */
const decodeInt24 = (rgbaData) => {
	//--------------------------------------------------------------------------
	// rgbaデータを取得して変換してnullチェック
	const value = rgbaToUnsigned(rgbaData);
	//--------------------------------------------------------------------------
	// 符号処理を加えて返す
	return value - (value < CONSTANTS.HALF_24 ? 0 : CONSTANTS.MOD_24);
}

//******************************************************************************
/**
 * 48bit符号なし整数のデコード
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {number}
 */
const decodeUint48 = (rgbaData) => {
	//--------------------------------------------------------------------------
	// 上位ビット・下位ビット変換
	const upper = rgbaToUnsigned(rgbaData.subarray(0, 4));
	const lower = rgbaToUnsigned(rgbaData.subarray(4, 8));
	//--------------------------------------------------------------------------
	// 結合して返す
	return upper * CONSTANTS.MOD_24 + lower;
}

//******************************************************************************
/**
 * 48bit符号付き整数のデコード
 * @param {Uint8Array|Uint8ClampedArray} rgbaData
 * @returns {number}
 */
const decodeInt48 = (rgbaData) => {
	//--------------------------------------------------------------------------
	// 符号無しでデコードして計算・nullチェック
	const value = decodeUint48(rgbaData);
	//--------------------------------------------------------------------------
	// 符号処理を加えて返す
	return value - (value < CONSTANTS.HALF_48 ? 0 : CONSTANTS.MOD_48);
}

//******************************************************************************
/**
 * DataPNGベースクラス
 */
class DataPngBase {
	//**************************************************************************
	/**
	 * コンストラクタ
	 */
	constructor() {
		//----------------------------------------------------------------------
		// 厳密な型定義のためメンバ変数を列挙
		this.ELEMENTS = undefined;
		this.MOD_24 = undefined;
		this.MOD_48 = undefined;
		this.HALF_24 = undefined;
		this.HALF_48 = undefined;
		this.pointer = 0;
		this.rawData = undefined;
		//----------------------------------------------------------------------
		// 不変の物理定数を読み取り専用で定義
		Object.defineProperties(this, {
			"ELEMENTS": { value: CONSTANTS.ELEMENTS, writable: false },
			"MOD_24": { value: CONSTANTS.MOD_24, writable: false },
			"MOD_48": { value: CONSTANTS.MOD_48, writable: false },
			"HALF_24": { value: CONSTANTS.HALF_24, writable: false },
			"HALF_48": { value: CONSTANTS.HALF_48, writable: false }
		});
	}

	//**************************************************************************
	/**
	 * 解析セッションごとの動的な状態を初期化
	 * @returns {this}
	 */
	initialize() {
		//----------------------------------------------------------------------
		// ポインタを戻して参照を返す
		this.pointer = 0;
		return this;
	}

	//**************************************************************************
	/**
	 * 現在のポインタ位置から指定されたピクセル数分のrgbaデータを抽出
	 * @param {number} pixelCount 取得ピクセル数
	 * @returns {Uint8Array|Uint8ClampedArray} 取得したピクセル分のRGBA配列
	 * @throws {Error} 取得ピクセルの終端が保持ピクセルの長さを超える場合
	 */
	getRgbaData(pixelCount) {
		//----------------------------------------------------------------------
		// 取得開始位置と終了位置の決定
		const start = this.pointer * this.ELEMENTS;
		const end = (this.pointer + pixelCount) * this.ELEMENTS;
		//----------------------------------------------------------------------
		// 終了位置チェック
		if (end > this.rawData.length) {
			throw new Error(
				`Out of bounds: pointer=${this.pointer}, pixelCount=${pixelCount}`
			);
		}
		//----------------------------------------------------------------------
		// ポインタを進めてsubarrayでビューとして取得して返す
		this.pointer += pixelCount;
		const data = this.rawData.subarray(start, end);
		return data;
	}
	//**************************************************************************
	/**
	 * 不明の場合もRGBAをそのまま返す
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {Uint8Array|Uint8ClampedArray}
	 */
	decodeUndefined(rgbaData) {
		//----------------------------------------------------------------------
		// rgbaDataの要素数は常に0か4か8
		if (rgbaData.length !== 4 && rgbaData.length !== 8) {
			throw new Error(`Argument array length must be 4 or 8.`);
		}
		//----------------------------------------------------------------------
		// データをそのまま返す
		return rgbaData;
	}

	//**************************************************************************
	/**
	 * RGBAをそのまま返す
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {Uint8Array|Uint8ClampedArray}
	 */
	decodeRgba(rgbaData) {
		return this.decodeUndefined(rgbaData);
	}

	//**************************************************************************
	/**
	 * 24bit符号なし整数のデコード
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {number|null}
	 * @throws {Error} 不正な引数が渡された場合に例外
	 */
	decodeUint24(rgbaData) {
		//----------------------------------------------------------------------
		// rgbaDataの要素数は常に4
		if (rgbaData.length !== 4) {
			throw new Error(`Argument array length must be 4.`);
		}
		//----------------------------------------------------------------------
		// nullチェック
		if (rgbaData[3] === 0) {
			return null;
		}
		//----------------------------------------------------------------------
		// 計算して返す
		return rgbaToUnsigned(rgbaData);
	}

	//**************************************************************************
	/**
	 * 24bit符号付き整数のデコード
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {number|null}
	 * @throws {Error} 不正な引数が渡された場合に例外
	 */
	decodeInt24(rgbaData) {
		//----------------------------------------------------------------------
		// rgbaDataの要素数は常に4
		if (rgbaData.length !== 4) {
			throw new Error(`Argument array length must be 4.`);
		}
		//----------------------------------------------------------------------
		// nullチェック
		if (rgbaData[3] === 0) {
			return null;
		}
		//----------------------------------------------------------------------
		// 計算して返す
		return decodeInt24(rgbaData);
	}

	//**************************************************************************
	/**
	 * 48bit符号なし整数のデコード
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {number|null}
	 * @throws {Error} 不正な引数が渡された場合に例外
	 */
	decodeUint48(rgbaData) {
		//----------------------------------------------------------------------
		// rgbaDataの要素数は常に8
		if (rgbaData.length !== 8) {
			console.log(rgbaData);
			throw new Error(`Argument array length must be 4.`);
		}
		//----------------------------------------------------------------------
		// nullチェック
		if (rgbaData[3] === 0 || rgbaData[7] === 0) {
			return null;
		}
		//----------------------------------------------------------------------
		// 計算して返す
		return decodeUint48(rgbaData);
	}

	//**************************************************************************
	/**
	 * 48bit符号付き整数のデコード
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData RGBA型付配列
	 * @returns {number|null}
	 * @throws {Error} 不正な引数が渡された場合に例外
	 */
	decodeInt48(rgbaData) {
		//----------------------------------------------------------------------
		// rgbaDataの要素数は常に8
		if (rgbaData.length !== 8) {
			throw new Error(`Argument array length must be 4.`);
		}
		//----------------------------------------------------------------------
		// nullチェック
		if (rgbaData[3] === 0 || rgbaData[7] === 0) {
			return null;
		}
		//----------------------------------------------------------------------
		// 計算して返す
		return decodeInt48(rgbaData);
	}
};

//******************************************************************************
// export
export {
	DataPngBase,
	CONSTANTS,
	rgbaToUnsigned,
	decodeRgba,
	decodeUndefined,
	decodeUint24,
	decodeInt24,
	decodeUint48,
	decodeInt48
};
